<template>
  <Dialog
    v-model="dialogVisible"
    :title="title"
    title-tag="h2"
    :dialog-size="dialogSize"
    :show-close="showClose"
    :dialog-type="dialogType"
    :header-icon-src="headerIconSrc"
    :hide-header-icon="!headerIconSrc"
    :close-on-overlay="closeOnOverlay"
    @close="handleClose"
  >
    <div class="py-6" >
      <div v-if="description">{{ description }}</div>
      <slot />
    </div>
    <template v-if="$slots.footer" #footer>
      <slot name="footer" />
    </template>
  </Dialog>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: '',
  },
  // Optional body description rendered above the slot content. Forwarded to
  // Dialog's `description` prop so single-paragraph modals can pass
  // their copy here instead of stuffing it into the default slot.
  description: {
    type: String,
    default: '',
  },
  // Optional 24x24 icon rendered to the left of the header title. Callers that
  // don't pass one keep the previous behaviour of no header icon at all
  // (Dialog would otherwise fall back to its own dialog-type glyph).
  headerIconSrc: {
    type: String,
    default: '',
  },
  // Kept on the props surface for backward compat with existing callers that
  // pass these — Dialog doesn't expose a header logo / custom header
  // colors, so they're effectively no-ops now. Behavioral parity for the
  // open/close/title path is preserved.
  titleLogo: {
    type: String,
    default: null,
  },
  width: {
    type: String,
    default: '640px',
  },
  headerBackgroundColor: {
    type: String,
    default: '#004879',
  },
  headerTextColor: {
    type: String,
    default: '#FFFFFF',
  },
  showClose: {
    type: Boolean,
    default: true,
  },
  // Forwarded to Dialog. Defaults to true, which is Dialog's own default, so
  // every existing caller keeps its current behaviour; modals that must not be
  // dismissed by a stray backdrop click (e.g. a value shown only once) opt out.
  closeOnOverlay: {
    type: Boolean,
    default: true,
  },
  dialogType: {
    type: String,
    default: 'primary',
  },
});

const emit = defineEmits(['update:modelValue', 'close']);

const dialogVisible = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
  },
});

// Map the legacy `width="Npx"` prop callers were passing onto Dialog's
// dialog-size enum. Per the docs:
//   extra-small ≤ 480px, small = 640px, medium = 970px, large = 1060px.
const dialogSize = computed(() => {
  const px = parseInt(String(props.width), 10);
  if (!Number.isFinite(px)) return 'small';
  if (px <= 480) return 'extra-small';
  if (px <= 700) return 'small';
  if (px <= 1000) return 'medium';
  return 'large';
});

const handleClose = () => {
  emit('update:modelValue', false);
  emit('close');
};
</script>

<style src="@/styles/my-practice.css"></style>
